<template>
  <div class="red-seal-container" :style="{ width: size + 'px', height: size + 'px' }">
    <div class="seal">
      <div class="seal-border"></div>
      <div class="seal-rays"></div>

      <div class="seal-circle-text" v-if="companyName">
        <span v-for="(char, index) in companyNameChars" :key="index"
              :style="getCharStyle(index, companyNameChars.length, sealRadius * 0.85, -30, 180, sealRadius * 0.23 )">
          {{ char }}
        </span>
      </div>

      <div class="seal-star">★</div>

      <div class="seal-date" :style="{ fontSize: sealRadius * 0.2 + 'px' }">{{ formattedSealDate }}</div>
    </div>
  </div>
</template>

<script>
export default {
  name: 'RedSeal',
  props: {
    companyName: {
      type: String,
      required: true,
      default: '碳链网格认证',
    },
    date: {
      type: [String, Date],
      required: true,
      default: () => new Date().toISOString().split('T')[0], // 默认当天日期 YYYY-MM-DD
    },
    size: {
      type: Number,
      default: 150, // 默认大小
    }
  },
  computed: {
    companyNameChars() {
      return this.companyName.split('');
    },
    sealRadius() {
      return this.size / 2;
    },
    formattedSealDate() {
      if (!this.date) return '';
      try {
        const d = new Date(this.date);
        const year = d.getFullYear();
        // 月份和日期不补零，符合公章习惯
        const month = d.getMonth() + 1;
        const day = d.getDate();
        return `${year}年${month}月${day}日`;
      } catch (e) {
        console.error('Invalid date prop for RedSeal:', this.date);
        return '日期无效';
      }
    },
  },
  methods: {
    getCharStyle(index, totalLength, radius, angleOffset = 0, arc = 360, fontSize) {
      // 调整弧度，使得文字在顶部半圆分布更合理
      const charSpacing = 0.9; // 根据字符数量和所需间隔调整
      const effectiveTotalLength = totalLength * charSpacing;
      const anglePerChar = arc / (effectiveTotalLength > 1 ? effectiveTotalLength -1 : 1);
      let angle = angleOffset + (index * anglePerChar) - (arc / 2) + (anglePerChar / 2) ; // 居中文字

      // 修正角度使其从顶部开始向两侧分布
      const topArcStartAngle = - (arc / 2); // 例如，如果arc是180，则从-90度开始
      angle = topArcStartAngle + (index * anglePerChar) + (anglePerChar / 2) - (anglePerChar * (1-charSpacing)/2) ;

      angle += angleOffset; // 应用整体角度偏移

      const x = Math.sin(angle * Math.PI / 180) * radius; // sin for x to start from top center
      const y = -Math.cos(angle * Math.PI / 180) * radius; // -cos for y

      const rotate = angle;

      return {
        position: 'absolute',
        left: `calc(50% + ${x}px)`,
        top: `calc(50% + ${y}px)`,
        transform: `translate(-50%, -50%) rotate(${rotate}deg)`,
        transformOrigin: 'center',
        color: '#ff0000', // 公章文字通常为红色
        fontWeight: 'bold',
        fontSize: `${fontSize || this.sealRadius * 0.15}px`,
        fontFamily: "'SimSun', 'Songti SC', serif", // 宋体或仿宋体
        zIndex: 5,
      };
    }
  }
};
</script>

<style scoped>
.red-seal-container {
  display: inline-block;
  position: relative;
}

.seal {
  width: 100%;
  height: 100%;
  border-radius: 50%;
  background-color: rgba(255, 255, 255, 0.0); /* 透明背景，依赖边框和文字显示红色 */
  color: #ff0000; /* 文字颜色 */
  display: flex;
  justify-content: center;
  align-items: center;
  position: relative;
  /* box-shadow: 0 0 5px rgba(0, 0, 0, 0.2); */
  /* border: 3px solid #cc0000;  改为使用seal-border */
  overflow: hidden; /* 确保内部元素不溢出容器 */
}

.seal-border {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  border: 3px solid #ff0000; /* 红色实线外边框，参考 cert/index.vue 是4px #e53935 */
  border-radius: 50%;
  box-sizing: border-box;
  z-index: 2;
}

.seal-rays {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  z-index: 1;
}

/* 模拟印章内部的虚线圆圈或细线圆圈 */
.seal-rays::before {
  content: '';
  position: absolute;
  top: 8%; /* 根据外边框和整体大小调整 */
  left: 8%;
  right: 8%;
  bottom: 8%;
  border: 1px dashed #ff0000; /* 虚线内圈，颜色比边框略浅或相同 */
  border-radius: 50%;
  opacity: 0.7;
}

/* 模拟印章模糊/放射效果，cert/index.vue 中有一个 repeating-conic-gradient */
.seal-rays::after {
  content: '';
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: repeating-conic-gradient(
    rgba(255, 0, 0, 0) 0deg 5deg, /* 透明 */
    rgba(255, 0, 0, 0.08) 5deg 10deg /* 非常浅的红色 */
  );
  border-radius: 50%;
  opacity: 0.6;
}

.seal-circle-text {
  position: absolute;
  width: 100%;
  height: 100%;
  top: 0;
  left: 0;
  z-index: 5;
  display: flex;
  justify-content: center;
  align-items: center;
  font-weight: bold;
  
  font-weight: bold;
  font-family: 'SimSun', '宋体', serif;
  text-shadow: 0 0 1px rgba(229, 57, 53, 0.5);
}

.seal-star {
  position: absolute;
  top: 50%;
  left: 50%;
  /* 五角星大小应根据整体图章大小动态调整 */
  font-size: calc(var(--seal-radius, 75px) * 0.8); /* 使用CSS变量或动态计算 */
  color: #ff0000;
  transform: translate(-50%, -50%);
  z-index: 3;
  /* 使用clip-path绘制五角星，参考cert/index.vue */
  /* 如果要用字符，确保字符大小合适 */
}

/* 另一种五角星实现方式，使用clip-path */
/*
.seal-star::before {
  content: '';
  position: absolute;
  top: 0;
  left: 0;
  width: 100%; (or a fixed size like 30px if font-size is not used for star)
  height: 100%; (or a fixed size like 30px)
  background-color: #ff0000;
  clip-path: polygon(50% 0%, 61% 35%, 98% 35%, 68% 57%, 79% 91%, 50% 70%, 21% 91%, 32% 57%, 2% 35%, 39% 35%);
  transform: translate(-50%, -50%) scale( (size_prop / 150) ); (Adjust scale based on size prop)
}
*/

.seal-date {
  position: absolute;
  bottom: 10%; /* 根据图章大小和文字布局调整 */
  left: 50%;
  transform: translateX(-50%);
  color: #ff0000;
  /* fontSize: 12px; 改为动态 */
  font-family: 'SimSun', 'Songti SC', serif; /* 日期也用宋体 */
  z-index: 4;
  white-space: nowrap;
  font-family: 'SimSun', '宋体', serif;
  text-shadow: 0 0 1px rgba(229, 57, 53, 0.5);
}

/* 覆盖默认的 .seal-text-top, .seal-text-bottom (如果之前有) */
/* 现在的文字通过 .seal-circle-text span 实现 */

</style> 